import { AppBar, Button, Collapse, CssBaseline, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography, useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { drawerWidth, useSideBarOptions } from '../../lib/static_values';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import logo_icon from '../../assets/logo_icon.png';
import UserService from '../../services/UserService';
import { Link, useLocation } from 'react-router-dom';
import ConditionalWrapper from '../ConditionalWrapper/ConditionalWrapper';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
}

const buildListLeaf = (
  option: {
    label: string, level: number, icon?: JSX.Element, link: string, externalLink?: boolean
  }
) => {
  const { pathname } = useLocation();  
  return (
    <>
      <Divider />
      <ConditionalWrapper
        condition={!option.externalLink}
        wrapper={(children) => <Link to={option.link}
          style={{ textDecoration: 'none', color: 'black' }}>
          {children}
        </Link>}
      >
        <ListItemButton tabIndex={12} 
          onClick={() => option.externalLink
            ? window.open(option.link, '_blank')
            : window.location.hash = `${option.link}`}
          selected={option.link === pathname}
        >
          {option.icon
            ? <ListItemIcon sx={{ minWidth: '30px' }}>
              {option.icon}
            </ListItemIcon>
            : null
          }
          <ListItemText primary={option.label} sx={{ pl: option.level * 3 }} />
          {option.externalLink && <OpenInNewIcon fontSize='small'/>}
        </ListItemButton>
      </ConditionalWrapper>
    </>
  );
};

const renderSidebar = (options: any) => {
  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  if (options.children && options.children.length > 0) {
    return (<>
      <Divider />
      <ListItemButton onClick={handleClick}>
        {options.icon
          ? <ListItemIcon sx={{ minWidth: '30px' }}>
            {options.icon}
          </ListItemIcon>
          : null
        }
        <ListItemText primary={options.label} sx={{ pl: options.level * 3 }} />
        {open
          ? <ExpandLess />
          : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout='auto'
        unmountOnExit>
        <List disablePadding>
          {options.children.map((child: any) => {
            return (
              renderSidebar(child)
            );
          })}
        </List>
      </Collapse>
    </>);
  } else {
    return (
      buildListLeaf(options)
    );
  }
};
const Sidebar = (props: Props) => {
  const { window } = props;
  const matchesMDbreakpoint = useMediaQuery('(min-width:768px)');
  const sideBarOptions = useSideBarOptions();

  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const container = window !== undefined
    ? () => window().document.body
    : undefined;

  const drawer = (
    <>
      <Toolbar />
      <Divider sx={{ mb: 0.2 }} />
      <List disablePadding sx={{ flexGrow: 1 }}>
        {sideBarOptions.map((options: any) => {
          return (
            renderSidebar(options)
          );
        })}
      </List>
      <Box>
        <Button fullWidth onClick={() => UserService.doLogout({redirectUri: `${process.env.REACT_APP_APP_URL}/admin-login`})}>Αποσύνδεση</Button>
      </Box>
    </>
  );

  // close the mobile drawer when screen size > md
  React.useEffect(() => {
    matchesMDbreakpoint && setMobileOpen(false);
  }, [matchesMDbreakpoint]);

  return (
    <>
      <CssBaseline />
      <AppBar
        position='fixed'
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <Toolbar >
          <IconButton
            color='inherit'
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <img src={logo_icon} alt='EKOME-logo'
            style={{ maxWidth: '65px', maxHeight: '65px' }} />
          <Typography ml={2} variant='h3'
            noWrap>
            Διαχειριστικό
          </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        {/* Responsive Drawer, when breakpoint < md */}
        <Drawer
          disablePortal
          container={container}
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        {/* Normal Drawer */}
        <Drawer
          disablePortal
          variant='permanent'
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

    </>
  );
};

export default Sidebar;